import { useEffect, useRef } from 'react'
// Subpath, NOT the '@edge/charts' barrel — this hook mounts eagerly (auth
// provider tree) and the barrel drags the klinecharts runtime into the entry
// bundle (see the '@edge/charts/interaction' precedent in vite.config.ts).
import {
  getAllIndicatorUserDefaults,
  replaceAllIndicatorUserDefaults,
  subscribeIndicatorUserDefaults,
} from '@edge/charts/user-defaults'
import type { IndicatorUserDefault } from '@edge/charts/user-defaults'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'

/**
 * Two-way sync of the per-indicator saved defaults ("Save current as
 * Default" in the indicator drawer) with the user's server profile
 * (`chartIndicatorDefaults`), so saved defaults follow the user across
 * devices. localStorage (`edge.chart.indicatorDefaults.v1`, inside
 * charts-core) stays the fast local cache; this layers the server on top.
 *
 * Unlike the theme / interaction syncs there is no React state to watch —
 * writes happen deep inside charts-core — so this hook listens on
 * `subscribeIndicatorUserDefaults` instead.
 *
 * - On first profile load: apply the server map wholesale (server wins on
 *   login). Only object-valued entries are applied — garbage is ignored.
 * - On later local changes: push the whole map up (deduped via
 *   JSON.stringify comparison).
 * - A user with no saved value triggers NO write on login — `lastSynced`
 *   seeds to the current local map, so only a genuine change syncs up.
 *
 * Mount once inside the authenticated tree (see AppLayout, next to
 * `useThemeServerSync` / `useChartInteractionServerSync`).
 */
export function useChartIndicatorDefaultsServerSync(): void {
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const updateUserRef = useRef(updateUser)
  updateUserRef.current = updateUser
  const initialized = useRef(false)
  const lastSynced = useRef<string | null>(null)

  // First profile load: server wins wholesale. Seeding lastSynced BEFORE
  // replaceAll means the notification it fires dedupes to a no-op instead of
  // echoing the server's own value back up.
  useEffect(() => {
    if (!user || initialized.current) return
    initialized.current = true
    const server = validateServerValue(user.chartIndicatorDefaults)
    if (server) {
      lastSynced.current = JSON.stringify(server)
      replaceAllIndicatorUserDefaults(server)
    } else {
      lastSynced.current = JSON.stringify(getAllIndicatorUserDefaults())
    }
  }, [user])

  // Local changes: push the whole map, deduped. Inert until the first
  // profile load has seeded lastSynced — a save made while logged out (or
  // before the profile arrives) syncs on the next change, not blindly.
  useEffect(() => {
    return subscribeIndicatorUserDefaults(() => {
      if (!initialized.current) return
      const map = getAllIndicatorUserDefaults()
      const serialized = JSON.stringify(map)
      if (serialized === lastSynced.current) return
      lastSynced.current = serialized
      void updateUserRef.current({ chartIndicatorDefaults: map })
    })
  }, [])
}

/** Loose validation: keep only entries whose value is a plain object (the
 *  IndicatorUserDefault shape is enforced by charts-core on read). Returns
 *  null when nothing valid is present. */
function validateServerValue(
  raw: Record<string, unknown> | null | undefined,
): Record<string, IndicatorUserDefault> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const out: Record<string, IndicatorUserDefault> = {}
  let hasAny = false
  for (const [name, value] of Object.entries(raw)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[name] = value as IndicatorUserDefault
      hasAny = true
    }
  }
  return hasAny ? out : null
}
